<template>
  <div
    class="file_drop_zone"
    :class="{ is_dragover: isDragOver }"
    @click="openFile"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <p class="file_drop_txt">
      파일 첨부 버튼을 클릭 하거나 또는 파일 Drag로 추가해주세요.
    </p>
    <button
      type="button"
      class="btn btn_xs btn_bd_primary"
      @click.stop="openFile"
    >
      파일첨부
    </button>

    <input
      ref="fileInput"
      class="hidden"
      type="file"
      multiple
      @change="onChange"
    />
  </div>

  <!-- 파일 리스트 -->
  <div class="file_upload_wrap" v-if="files.length">
    <div class="file_item" v-for="(f, idx) in files" :key="`${f.kind}-${f.id}`">
      <a
        href="javascript:void(0);"
        class="file_name f14 ellipsis2"
        :title="f.name"
        @click.prevent="downloadFile(f)"
      >
        {{ f.name }}
      </a>

      <Base.Button icon="delete_file" txt="none" @click="removeFile(idx)">
        삭제
      </Base.Button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, watch } from "vue";
import { Base } from "@/common/base-components/BaseFactory";

const props = defineProps<{
  fileList?: { id: string | number; name: string; url: string }[];
}>();

type UploadItem =
  | { kind: "server"; id: string | number; name: string; url: string }
  | { kind: "local"; id: string; name: string; raw: File };

const fileInput = ref<HTMLInputElement | null>(null);
const files = ref<UploadItem[]>([]);
const isDragOver = ref(false);

watch(
  () => props.fileList,
  (list) => {
    const serverFiles: UploadItem[] = (list ?? []).map((f) => ({
      kind: "server",
      id: f.id,
      name: f.name,
      url: f.url,
    }));

    const localFiles = files.value.filter((x) => x.kind === "local");
    files.value = [...serverFiles, ...localFiles];
  },
  { immediate: true },
);

const openFile = () => fileInput.value?.click();

const resetFileInput = () => {
  if (fileInput.value) fileInput.value.value = "";
};

const addFiles = (list: FileList | File[]) => {
  Array.from(list).forEach((f) => {
    files.value.push({
      kind: "local",
      id: crypto.randomUUID(),
      name: f.name,
      raw: f,
    });
  });
};

const onChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (!target.files?.length) return;

  addFiles(target.files);
  resetFileInput();
};

const onDragEnter = () => (isDragOver.value = true);
const onDragOver = () => (isDragOver.value = true);
const onDragLeave = () => (isDragOver.value = false);

const onDrop = (e: DragEvent) => {
  isDragOver.value = false;

  const dtFiles = e.dataTransfer?.files;
  if (!dtFiles?.length) return;

  addFiles(dtFiles);
  resetFileInput();
};

const removeFile = (idx: number) => {
  files.value.splice(idx, 1);
};

const downloadFile = (f: UploadItem) => {
  if (f.kind === "server") {
    window.open(f.url, "_blank");
    return;
  }

  const url = URL.createObjectURL(f.raw);
  const a = document.createElement("a");
  a.href = url;
  a.download = f.name;
  a.click();
  URL.revokeObjectURL(url);
};

/** 로컬 파일 목록 반환 */
const getLocalFiles = (): File[] => {
  return files.value
    .filter((f): f is Extract<UploadItem, { kind: "local" }> => f.kind === "local")
    .map((f) => f.raw);
};

/** 파일 초기화 */
const resetFiles = () => {
  files.value = [];
};

defineExpose({
  getLocalFiles,
  resetFiles,
});
</script>
